// ui/src/components/manager/RepCompDetail.jsx
/**
 * @file RepCompDetail.jsx
 * @module ui/src/components/manager/RepCompDetail
 * @description Displays a manager's individual rep compensation detail, provisional earnings, temporal coverage, readiness, and person-scoped draw ledger.
 *
 * @session-6-migration-flags
 * - plan.effective_from / effective_to (m_tc_01): NOT IN DDL r57. Temporal
 *   relationship is rendered from server-supplied planCoverage fields only.
 * - draw_ledger.participant_uuid (m_tc_10): NOT IN DDL r57. PersonDrawLedger
 *   is invoked with position.personUuid; its server endpoint must resolve
 *   person-scoped continuity.
 * - draw_ledger_entry (m_tc_11): NOT IN DDL r57. Full ledger rendering is
 *   delegated to C-58 and remains migration-aware there.
 *
 * @locked-rules
 * - INV-6: Draw balance follows the person, not the plan.
 * - INV-3: planStatus is never used for temporal display.
 * - DP-8: Amendment language is document-level only.
 * - XC-6: Provisional zero, unavailable, and partial results remain distinct.
 * - DP-10: This component performs no optimistic server mutation.
 *
 * @accessibility
 * - Panel uses role="region" with rep-specific aria-label.
 * - Draw ledger opens in an aria-modal dialog.
 * - Dialog traps focus, closes on Escape, and restores trigger focus.
 *
 * @dependencies
 * - C-58 PersonDrawLedger.
 * - C-46 DataFreshnessIndicator.
 * - C-44 ProvisionalBadge.
 */

import {
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import PersonDrawLedger from '../shared/PersonDrawLedger';
import DataFreshnessIndicator from '../shared/DataFreshnessIndicator';
import ProvisionalBadge from '../shared/ProvisionalBadge';

/**
 * @typedef {Object} RepCompDetailProps
 * @property {string} positionId
 * @property {number} fiscalPeriodId
 */

function formatCurrency(value, currencyCode) {
  if (value === null || value === undefined) return '—';

  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode || 'USD',
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value) {
  if (value === null || value === undefined) return '—';

  return `${new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(value)}%`;
}

function titleCase(value) {
  if (!value) return 'Unknown';

  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function useFocusTrap(open, dialogRef, restoreRef, onClose) {
  useEffect(() => {
    if (!open || !dialogRef.current) return undefined;

    const dialog = dialogRef.current;
    const previousFocus = restoreRef.current;

    const getFocusable = () =>
      Array.from(
        dialog.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );

    const focusable = getFocusable();
    (focusable[0] || dialog).focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const currentFocusable = getFocusable();
      if (currentFocusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = currentFocusable[0];
      const last = currentFocusable[currentFocusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    dialog.addEventListener('keydown', handleKeyDown);

    return () => {
      dialog.removeEventListener('keydown', handleKeyDown);
      if (previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus();
      }
    };
  }, [open, dialogRef, restoreRef, onClose]);
}

function RepCompDetail({ positionId, fiscalPeriodId }) {
  const [data, setData] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  const dialogRef = useRef(null);
  const ledgerTriggerRef = useRef(null);

  const closeDrawer = () => setDrawerOpen(false);

  useFocusTrap(
    drawerOpen,
    dialogRef,
    ledgerTriggerRef,
    closeDrawer
  );

  useEffect(() => {
    const controller = new AbortController();

    setLoading(true);
    setError(null);

    fetch(
      `/api/v1/manager/rep/${encodeURIComponent(
        positionId
      )}/detail?period=${encodeURIComponent(fiscalPeriodId)}`,
      { signal: controller.signal }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Unable to load rep compensation detail (${response.status}).`
          );
        }

        return response.json();
      })
      .then((payload) => {
        setData(payload);
      })
      .catch((fetchError) => {
        if (fetchError.name !== 'AbortError') {
          setError(fetchError);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [positionId, fiscalPeriodId, retryKey]);

  if (loading) {
    return (
      <section
        className="rep-comp-detail"
        aria-label="Rep compensation detail"
        aria-busy="true"
      >
        <div className="skeleton rep-comp-detail__header-skeleton" />
        <div className="rep-comp-detail__metric-grid">
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              className="skeleton rep-comp-detail__metric-skeleton"
            />
          ))}
        </div>
        <div className="skeleton rep-comp-detail__section-skeleton" />
        <style>{styles}</style>
      </section>
    );
  }

  if (error) {
    return (
      <section
        className="rep-comp-detail__error"
        role="alert"
        data-state="error"
      >
        <h3>Unable to load rep compensation detail</h3>
        <p>{error.message}</p>
        <button
          type="button"
          className="secondary-button"
          onClick={() => setRetryKey((value) => value + 1)}
        >
          Retry
        </button>
        <style>{styles}</style>
      </section>
    );
  }

  if (!data) {
    return (
      <section
        className="rep-comp-detail__empty"
        data-state="unavailable"
      >
        Rep compensation detail is not available.
        <style>{styles}</style>
      </section>
    );
  }

  const payout = data.payout ?? {};
  const partialPayout = payout.provisionalState === 'partial';
  const payoutAvailable =
    payout.provisionalPayout !== null &&
    payout.provisionalPayout !== undefined;

  return (
    <section
      className="rep-comp-detail"
      role="region"
      aria-label={`Rep Compensation Detail — ${data.position.fullName}`}
    >
      <header className="rep-comp-detail__header">
        <div>
          <p className="rep-comp-detail__eyebrow">Rep compensation</p>
          <h2>{data.position.fullName}</h2>
          <p>
            {data.position.roleCode} · {data.position.segmentCode}
          </p>
        </div>

        <span
          className="rep-comp-detail__period"
          data-state={data.period.periodStatus}
        >
          {data.period.periodLabel}
        </span>
      </header>

      <div className="rep-comp-detail__metric-grid">
        <section
          className="rep-comp-detail__metric"
          aria-label={`Quota ${formatCurrency(
            data.quota?.quotaReporting,
            data.quota?.quotaCurrencyCode
          )}`}
        >
          <span>Quota</span>
          <strong>
            {formatCurrency(
              data.quota?.quotaReporting,
              data.quota?.quotaCurrencyCode
            )}
          </strong>
        </section>

        <section
          className="rep-comp-detail__metric"
          aria-label={`Credited performance ${formatCurrency(
            data.attainment?.creditTotalReporting,
            data.attainment?.creditCurrencyCode
          )}`}
        >
          <span>Credited performance</span>
          <strong>
            {formatCurrency(
              data.attainment?.creditTotalReporting,
              data.attainment?.creditCurrencyCode
            )}
          </strong>
        </section>

        <section
          className="rep-comp-detail__metric"
          aria-label={`Attainment ${formatPercent(
            data.attainment?.attainmentPct
          )}`}
        >
          <span>Attainment</span>
          <strong>{formatPercent(data.attainment?.attainmentPct)}</strong>
        </section>
      </div>

      <section
        className="rep-comp-detail__section"
        aria-labelledby="rep-comp-detail-payout"
      >
        <div className="rep-comp-detail__section-heading">
          <div>
            <p className="rep-comp-detail__eyebrow">Projected earnings</p>
            <h3 id="rep-comp-detail-payout">Provisional payout</h3>
          </div>

          {payout.freshnessAt ? (
            <ProvisionalBadge
              freshnessTimestamp={payout.freshnessAt}
              label="Provisional"
              completenessState={
                payout.provisionalState === 'full' ? 'full' : 'partial'
              }
            />
          ) : null}
        </div>

        <div
          className="rep-comp-detail__payout-value"
          data-state={
            !payoutAvailable
              ? 'unavailable'
              : partialPayout
                ? 'partial'
                : 'calculated'
          }
        >
          <strong>
            {payoutAvailable
              ? formatCurrency(
                  payout.provisionalPayout,
                  payout.payoutCurrencyCode
                )
              : 'Not available'}
          </strong>

          {partialPayout && payoutAvailable ? (
            <span className="rep-comp-detail__partial-label">Partial</span>
          ) : null}
        </div>

        {payout.nextThreshold ? (
          <p className="rep-comp-detail__threshold">
            {formatCurrency(
              payout.nextThreshold.amountNeeded,
              payout.nextThreshold.currencyCode
            )}{' '}
            to {payout.nextThreshold.label}
          </p>
        ) : null}
      </section>

      <section
        className="rep-comp-detail__section"
        aria-labelledby="rep-comp-detail-coverage"
      >
        <div className="rep-comp-detail__section-heading">
          <h3 id="rep-comp-detail-coverage">Plan coverage</h3>
          <span
            className={`rep-comp-detail__coverage-badge rep-comp-detail__coverage-badge--${data.planCoverage.coverageStatus}`}
            data-state={data.planCoverage.coverageStatus}
          >
            {titleCase(data.planCoverage.coverageStatus)}
          </span>
        </div>

        <dl className="rep-comp-detail__detail-grid">
          <div>
            <dt>Plan</dt>
            <dd>{data.planCoverage.planName || '—'}</dd>
          </div>
          <div>
            <dt>Version</dt>
            <dd>{data.planCoverage.planVersionLabel || '—'}</dd>
          </div>
          <div>
            <dt>Relationship</dt>
            <dd>{titleCase(data.planCoverage.temporalRelationship)}</dd>
          </div>
        </dl>

        {data.planCoverage.coverageStatus !== 'covered' ? (
          <p
            className="rep-comp-detail__coverage-warning"
            data-state="coverage-issue"
          >
            Plan coverage requires attention for this period.
          </p>
        ) : null}
      </section>

      {data.amendment?.inFlight === true ? (
        <section
          className="rep-comp-detail__amendment"
          data-state="amendment-pending-review"
          aria-label="Pending plan amendment"
        >
          <strong>Amendment pending review</strong>
          <span>
            {data.amendment.whatChanged
              ? ` — ${data.amendment.whatChanged}`
              : ''}
          </span>
        </section>
      ) : null}

      {data.drawSummary?.hasDrawProgram === true ? (
        <section
          className="rep-comp-detail__section"
          aria-labelledby="rep-comp-detail-draw"
        >
          <div className="rep-comp-detail__section-heading">
            <div>
              <h3 id="rep-comp-detail-draw">Draw balance</h3>
              <p>Person-level balance · continues across plan amendments.</p>
            </div>

            <button
              ref={ledgerTriggerRef}
              type="button"
              className="secondary-button"
              aria-label={`View draw ledger for ${data.position.fullName}`}
              onClick={() => setDrawerOpen(true)}
            >
              View full ledger
            </button>
          </div>

          <strong className="rep-comp-detail__draw-value">
            {formatCurrency(
              data.drawSummary.currentBalance,
              data.drawSummary.currencyCode
            )}
          </strong>
        </section>
      ) : null}

      {data.readiness?.freshnessAt ? (
        <section
          className="rep-comp-detail__section"
          aria-labelledby="rep-comp-detail-readiness"
        >
          <h3 id="rep-comp-detail-readiness">Data readiness</h3>
          <DataFreshnessIndicator
            signal={data.readiness.signal}
            freshnessAt={data.readiness.freshnessAt}
            status={data.readiness.status}
          />
          {data.readiness.blockingIssueCount > 0 ? (
            <p
              className="rep-comp-detail__blocking"
              data-state="blocking"
            >
              {data.readiness.blockingIssueCount}{' '}
              {data.readiness.blockingIssueCount === 1
                ? 'blocking issue'
                : 'blocking issues'}
            </p>
          ) : null}
        </section>
      ) : null}

      {drawerOpen && data.drawSummary?.hasDrawProgram === true ? (
        <div
          className="rep-comp-detail__dialog-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeDrawer();
            }
          }}
        >
          <div
            ref={dialogRef}
            className="rep-comp-detail__dialog"
            role="dialog"
            aria-modal="true"
            aria-label={`Draw ledger for ${data.position.fullName}`}
            tabIndex={-1}
          >
            <header className="rep-comp-detail__dialog-header">
              <div>
                <h3>Draw ledger</h3>
                <p>
                  Balance continues across plan amendments.
                </p>
              </div>

              <button
                type="button"
                className="icon-button"
                aria-label="Close draw ledger"
                onClick={closeDrawer}
              >
                ×
              </button>
            </header>

            <div className="rep-comp-detail__dialog-body">
              <PersonDrawLedger
                personUuid={data.position.personUuid}
                fiscalPeriodId={fiscalPeriodId}
                mode="manager"
              />
            </div>
          </div>
        </div>
      ) : null}

      <style>{styles}</style>
    </section>
  );
}

RepCompDetail.propTypes = {
  positionId: PropTypes.string.isRequired,
  fiscalPeriodId: PropTypes.number.isRequired,
};

const styles = `
  .rep-comp-detail {
    display: flex;
    flex-direction: column;
    gap: 12px;
    color: rgb(var(--color-text));
  }

  .rep-comp-detail__header,
  .rep-comp-detail__section {
    border: 1px solid rgb(var(--color-border));
    border-radius: 12px;
    background: rgb(var(--color-surface));
  }

  .rep-comp-detail__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
  }

  .rep-comp-detail__header h2 {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
  }

  .rep-comp-detail__header p:not(.rep-comp-detail__eyebrow) {
    margin: 3px 0 0;
    color: rgb(var(--color-text-muted));
    font-size: 12px;
  }

  .rep-comp-detail__eyebrow {
    margin: 0 0 3px;
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .rep-comp-detail__period {
    padding: 2px 10px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
  }

  .rep-comp-detail__metric-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }

  .rep-comp-detail__metric {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 5px;
    padding: 14px 16px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 12px;
    background: rgb(var(--color-surface));
  }

  .rep-comp-detail__metric span {
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .rep-comp-detail__metric strong {
    font-size: 17px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .rep-comp-detail__section {
    padding: 16px;
  }

  .rep-comp-detail__section h3 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
  }

  .rep-comp-detail__section-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .rep-comp-detail__section-heading p {
    margin: 3px 0 0;
    color: rgb(var(--color-text-muted));
    font-size: 11px;
  }

  .rep-comp-detail__payout-value {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
  }

  .rep-comp-detail__payout-value strong {
    font-size: 24px;
    font-weight: 600;
  }

  .rep-comp-detail__partial-label {
    padding: 2px 10px;
    border: 1px solid rgb(var(--color-warning) / 0.3);
    border-radius: 999px;
    background: rgb(var(--color-warning) / 0.07);
    color: rgb(var(--color-warning));
    font-size: 11px;
    font-weight: 600;
  }

  .rep-comp-detail__threshold {
    margin: 8px 0 0;
    color: rgb(var(--color-text-muted));
    font-size: 12px;
  }

  .rep-comp-detail__detail-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin: 14px 0 0;
  }

  .rep-comp-detail__detail-grid dt {
    margin-bottom: 3px;
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .rep-comp-detail__detail-grid dd {
    margin: 0;
    font-size: 12px;
  }

  .rep-comp-detail__coverage-badge {
    padding: 2px 10px;
    border: 1px solid rgb(var(--color-border));
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
  }

  .rep-comp-detail__coverage-badge--covered {
    border-color: rgb(var(--color-success) / 0.25);
    background: rgb(var(--color-success) / 0.05);
    color: rgb(var(--color-success));
  }

  .rep-comp-detail__coverage-warning,
  .rep-comp-detail__blocking {
    margin: 12px 0 0;
    padding: 10px 12px;
    border: 1px solid rgb(var(--color-warning) / 0.25);
    border-radius: 8px;
    background: rgb(var(--color-warning) / 0.06);
    font-size: 12px;
  }

  .rep-comp-detail__amendment {
    padding: 12px 14px;
    border: 1px solid rgb(var(--color-warning) / 0.25);
    border-radius: 12px;
    background: rgb(var(--color-warning) / 0.06);
    font-size: 12px;
  }

  .rep-comp-detail__draw-value {
    display: block;
    margin-top: 12px;
    font-size: 20px;
    font-variant-numeric: tabular-nums;
  }

  .rep-comp-detail__dialog-backdrop {
    position: fixed;
    z-index: 1000;
    inset: 0;
    display: flex;
    justify-content: flex-end;
    background: rgb(0 0 0 / 0.35);
  }

  .rep-comp-detail__dialog {
    width: min(520px, 100%);
    height: 100%;
    overflow-y: auto;
    border-left: 1px solid rgb(var(--color-border));
    background: rgb(var(--color-bg));
    box-shadow: -8px 0 30px rgb(0 0 0 / 0.14);
  }

  .rep-comp-detail__dialog-header {
    position: sticky;
    z-index: 1;
    top: 0;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid rgb(var(--color-border));
    background: rgb(var(--color-surface));
  }

  .rep-comp-detail__dialog-header h3 {
    margin: 0;
    font-size: 14px;
  }

  .rep-comp-detail__dialog-header p {
    margin: 3px 0 0;
    color: rgb(var(--color-text-muted));
    font-size: 11px;
  }

  .rep-comp-detail__dialog-body {
    padding: 16px;
  }

  .rep-comp-detail button:focus-visible,
  .rep-comp-detail__dialog:focus-visible {
    outline: 2px solid rgb(var(--color-focus-ring));
    outline-offset: 2px;
  }

  .rep-comp-detail__error {
    padding: 16px 12px;
    border: 1px solid rgb(var(--color-danger) / 0.2);
    border-radius: 8px;
    background: rgb(var(--color-danger) / 0.05);
  }

  .rep-comp-detail__error h3 {
    margin: 0 0 5px;
    font-size: 13px;
  }

  .rep-comp-detail__error p {
    margin: 0 0 12px;
    color: rgb(var(--color-text-muted));
    font-size: 12px;
  }

  .rep-comp-detail__empty {
    padding: 24px 16px;
    border: 1px dashed rgb(var(--color-border));
    border-radius: 8px;
    background: rgb(var(--color-surface-muted));
    color: rgb(var(--color-text-muted));
    font-size: 12px;
  }

  .rep-comp-detail__header-skeleton {
    height: 72px;
    border-radius: 12px;
  }

  .rep-comp-detail__metric-skeleton {
    height: 82px;
    border-radius: 12px;
  }

  .rep-comp-detail__section-skeleton {
    height: 140px;
    border-radius: 12px;
  }

  @media (max-width: 680px) {
    .rep-comp-detail__metric-grid,
    .rep-comp-detail__detail-grid {
      grid-template-columns: 1fr;
    }

    .rep-comp-detail__section-heading {
      flex-direction: column;
    }
  }
`;

export default RepCompDetail;
